import React, { useState, useEffect } from 'react';
import moment from 'moment';
import ArthaGateway from 'service/ArthaGateway';
import { CreditNote, Sale, SaleOrder, Receipt, VoucherSummary, Party, OrderStatuses } from 'service';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { faCalendar, faCircleCheck, faF, faFileInvoice, faIndustry, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { durations } from 'service/Utils';
import OrderStatusProgress from '../Widgets/OrderStatusProgress';
import { NavLink } from 'react-router-dom';

export const Orders = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [party, setParty] = useState<Party>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [durationId, setDurationId] = useState<string>(moment().month() > 3 ? moment().year().toString() : moment().subtract(1, 'year').year().toString());
    const [orders, setOrders] = useState<SaleOrder[]>([]);
    const [voucherSummary, setVoucherSummary] = useState<VoucherSummary>();
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(5);
    const [orderSummary, setOrderSummary] = useState<any[]>([]);

    useEffect(() => {
        if (user.profile) {
            setParty(user.profile.profile);
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadOrders();
    }, [party, pageNumber]);

    useEffect(() => {
        setOrders([]);
        loadOrders();
    }, [durationId]);

    const loadOrders = () => {
        if (!party || !party.id || party.id <= 0) {
            return;
        }
        setIsLoading(true);
        ArthaGateway
            .getOrders(party?.id, durationId, 0, 0, pageNumber, pageSize)
            .then((data) => {
                data.entries.map((order) => order.isExpanded = false);
                if (pageNumber > 1) {
                    setOrders(orders.concat(data.entries));
                }
                else {
                    setOrders(data.entries);
                }

                setTotalPages(data.totalPages);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const loadOrderSummary = () => {
        if (!party || !party.id || party.id <= 0) {
            return;
        }
        setIsLoading(true);
        ArthaGateway
            .getOrdersSummary(party?.id, durationId, 0, 0)
            .then((data) => {
                let d = {
                    "numberOfInvoices": data.numberOfIvoices,
                    "billAmount": data.billAmount,
                }
                let orderSummary = [
                    {
                        "statusCode": 1,
                        "status": "Pending",
                        "count": 0,
                    },
                    {
                        "statusCode": 2,
                        "status": "Partial",
                        "count": 0,
                    },
                    {
                        "statusCode": 3,
                        "status": "Fullfilled",
                        "count": 0,
                    },
                    {
                        "statusCode": 4,
                        "status": "Cancelled",
                        "count": 0,
                    }
                ];
                data.statusSummary.map((orderStatus) => {
                    orderSummary.map((summary) => {
                        if (summary.statusCode === orderStatus.status) {
                            summary.count = orderStatus.count;
                        }
                    });
                });
                setOrderSummary(orderSummary);
                setVoucherSummary(d);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const viewOrderData = (salesOrder: SaleOrder) => {
        ArthaGateway
            .getSalesOrderFileLink(salesOrder.id)
            .then((url) => {
                window.open(url, "_blank");
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const currencyFormat = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page-receipts'>
            <div className="row">
                <div className="col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4">
                    <div className="card bg-dark text-white mb-2">
                        <div className="card-header">
                            <h2 className="fw-bold mb-4">Orders</h2>
                            <div>
                                <select className="form-control" id="duration" value={durationId} onChange={(e) => setDurationId(e.target.value)}>
                                    {durations.map((duration) => (
                                        <option key={duration.value} value={duration.value}>{duration.label}</option>
                                    ))}
                                    <option value="-1">Other</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className='col-12 col-md-6 col-lg-4 offset-md-3 offset-lg-4'>
                    {isLoading &&
                        <div className="text-center" style={{ padding: 20 }}>
                            <div className="spinner-border text-dark" role="status">
                            </div>
                        </div>
                    }
                    {orders.length === 0 && !isLoading &&
                        <div className="alert alert-danger text-center" style={{ margin: 10 }}>
                            No orders found
                        </div>
                    }
                    {orders && orders.map((order) => (
                        <div className='card my-2' key={order.id}>
                            <div className="card-header text-center">
                                <h2 className="fw-bold">{order.voucherNumber}</h2>
                            </div>
                            <div className="card-body p-0">
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        <tr className="align-middle">
                                            <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                <FontAwesomeIcon icon={faCalendar} />
                                            </td>
                                            <td colSpan={2}>
                                                Date:
                                                <div className="fw-bold">{moment(order.date).format("DD MMM YYYY")}</div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ width: "60px", fontSize: "1.5rem", textAlign: "center" }}>
                                                <FontAwesomeIcon icon={faCircleCheck} />
                                            </td>
                                            <td colSpan={2}>
                                                Status:
                                                <div className="fw-bold">
                                                    {OrderStatuses.filter(s => s.id === order.status).map((s) => (
                                                        <span key={s.id} className={s.class}>{s.name}</span>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer">
                                <NavLink className="btn btn-dark w-100" title="View" to={`/order/${order.id}`}>
                                    <FontAwesomeIcon icon={faFileInvoice} style={{ width: "auto" }} />
                                    &nbsp; View Details
                                </NavLink>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="card-footer">
                    {(totalPages > pageNumber) && <>
                        {isLoading &&
                            <div className="btn btn-dark w-100 text-center">
                                <div className="spinner-border text-light" role="status">
                                </div>
                            </div>
                        }
                        {!isLoading &&
                            <div className="btn btn-dark w-100 text-center" onClick={() => setPageNumber(pageNumber + 1)}>Load More...</div>
                        }
                    </>
                    }
                </div>
            </div>
        </div>
    );
};